import React from 'react';
import { Box } from '@mui/material';

import { TemplateSection, TemplateField, DragData, Template } from '../../_types';
import SortableSection from './SortableSection';
import SectionInsertableDropZone from './SectionInsertableDropZone';

interface TemplateSectionsProps {
  sections: TemplateSection[];
  parentTemplate?: Template | null;
  onRemoveField: (sectionId: number, fieldId: number) => void;
  onToggleCollapsed: (sectionId: number) => void;
  isReorderMode: boolean;
  onRemoveSection: (sectionId: number) => Promise<void>;
  onSetSelectedField: (field: TemplateField) => void;
  onSetSelectedSection: (section: TemplateSection) => void;
  activeId?: string | null; // Add activeId to show drop zones
  dragData?: DragData | null; // Add dragData to determine drag type
  openFieldOptionsId: string | null;
  setOpenFieldOptionsId: (fieldId: string | null) => void;
  inactiveFieldIds?: Set<number>;
  duplicateFieldIds?: Set<number>;
  duplicateSectionIds?: Set<number>;
  emptySectionIds?: Set<number>;
  removableChildFieldIds?: Set<number>;
  selectedSectionId?: number | null;
  selectedSectionGuid?: string | null;
}

const TemplateSections: React.FC<TemplateSectionsProps> = ({
  sections,
  parentTemplate,
  onRemoveField,
  onToggleCollapsed,
  isReorderMode,
  onRemoveSection,
  onSetSelectedField,
  onSetSelectedSection,
  activeId,
  dragData,
  openFieldOptionsId,
  setOpenFieldOptionsId,
  inactiveFieldIds = new Set(),
  duplicateFieldIds = new Set(),
  duplicateSectionIds = new Set(),
  emptySectionIds = new Set(),
  removableChildFieldIds = new Set(),
  selectedSectionId,
  selectedSectionGuid,
}) => {
  // Filter out deleted sections (check both isDeleted and effectiveIsDeleted for inherited deletions)
  const visibleSections = sections.filter((section) => !section.isDeleted && !section.effectiveIsDeleted);
  const canDelete = visibleSections.length > 1;

  const isSectionDragActive = false;

  return (
    <Box sx={{ display: 'grid', gap: 4 }}>
      {/* Insert drop zone at the beginning */}
      <SectionInsertableDropZone
        insertIndex={0}
        isVisible={isSectionDragActive}
      />

      {visibleSections.map((section, index) => (
        <React.Fragment key={section.id === 0 ? `temp-section-${section.sectionGuid}` : section.id}>
          <SortableSection
            section={section}
            sections={visibleSections}
            parentTemplate={parentTemplate}
            onRemoveField={onRemoveField}
            onToggleCollapsed={onToggleCollapsed}
            isReorderMode={isReorderMode}
            onRemoveSection={onRemoveSection}
            canDelete={canDelete}
            onSetSelectedField={onSetSelectedField}
            onSetSelectedSection={onSetSelectedSection}
            activeId={activeId}
            dragData={dragData}
            openFieldOptionsId={openFieldOptionsId}
            setOpenFieldOptionsId={setOpenFieldOptionsId}
            inactiveFieldIds={inactiveFieldIds}
            duplicateFieldIds={duplicateFieldIds}
            duplicateSectionIds={duplicateSectionIds}
            emptySectionIds={emptySectionIds}
            removableChildFieldIds={removableChildFieldIds}
            selectedSectionId={selectedSectionId}
            selectedSectionGuid={selectedSectionGuid}
          />
          {/* Insert drop zone after each section */}
          <SectionInsertableDropZone
            insertIndex={index + 1}
            isVisible={isSectionDragActive}
          />
        </React.Fragment>
      ))}
    </Box>
  );
};

export default TemplateSections;
